// vim: set ts=2 sts=2 sw=2 et:
//
// Cada pestaña de OpenLifter tiene su propia copia del torneo en memoria, y redux-persist guarda la copia
// ENTERA en una sola clave. Con dos pestañas abiertas, la que guardaba última pisaba lo que había hecho la
// otra (Ibero 2026). Acá cada pestaña se entera de lo que guardó otra (evento `storage`) y lo toma antes de
// volver a guardar, así ninguna escribe un torneo viejo.

import type { Store } from "redux";

import { overwriteStore } from "../../actions/globalActions";
import type { GlobalState } from "../../types/stateTypes";
import { isWrpfChromeFreePath } from "../isWrpfChromeFreePath";

export const PERSIST_KEY = "persist:root";

// Lo que comparten las pestañas. wrpfLive no: la tarima en vivo es de cada pestaña y nunca se restaura de lo
// guardado (al rehidratar se apaga a propósito). Versions la fija el código de cada pestaña.
const SYNCED_SLICES = ["language", "meet", "registration", "lifting", "wrpfSession", "wrpfRecords"] as const;

// Un torneo guardado por otra pestaña nunca debería rebotar más de una o dos veces; si pasa, se corta.
const MAX_APPLIES = 5;
const APPLY_WINDOW_MS = 2000;

/** Lo que otra pestaña guardó distinto de esta; null si no hay nada que tomar. */
export function slicesFromOtherTab(serialized: string | null, current: GlobalState): Partial<GlobalState> | null {
  // Otra pestaña borró los datos (Depurar): esta no se vacía por eso.
  if (!serialized) return null;
  let stored: Record<string, unknown>;
  try {
    stored = JSON.parse(serialized) as Record<string, unknown>;
  } catch {
    return null;
  }
  const changes: Record<string, unknown> = {};
  for (const key of SYNCED_SLICES) {
    const raw = stored[key];
    if (typeof raw !== "string" || raw === JSON.stringify(current[key])) continue;
    try {
      changes[key] = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return Object.keys(changes).length > 0 ? (changes as Partial<GlobalState>) : null;
}

/** Escucha lo que guardan las otras pestañas y lo aplica a esta. Devuelve cómo dejar de escuchar. */
export function startCrossTabSync(store: Store): () => void {
  let applies: number[] = [];
  const onStorage = (event: StorageEvent) => {
    if (event.key !== PERSIST_KEY) return;
    const current = store.getState() as GlobalState;
    const changes = slicesFromOtherTab(event.newValue, current);
    if (changes === null) return;
    const now = Date.now();
    applies = applies.filter((at) => now - at < APPLY_WINDOW_MS);
    if (applies.length >= MAX_APPLIES) return;
    applies.push(now);
    store.dispatch(overwriteStore({ ...current, ...changes }));
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}

/**
 * Las pantallas de jueces, espectador, transmisión y lab no editan el torneo: no guardan, así una pestaña de
 * esas abierta en la PC del operador nunca pisa lo que guarda la operación.
 */
export const shouldPersistOnPath = (pathname: string): boolean => !isWrpfChromeFreePath(pathname);
